// Libs
import { useEffect } from 'react';
import { SWRResponse } from 'swr';
import axios, { AxiosError, AxiosResponse } from 'axios';
// App
import {
  ACCOUNT_URL,
  ACCOUNT_ACTIVATE_URL,
  ACCOUNT_REGISTER_URL,
  ACCOUNT_RESET_PASSWORD_FINISH_URL,
  ACCOUNT_RESET_PASSWORD_INIT_URL,
  ACCOUNT_RESET_PASSWORD_INIT_ADMIN_URL,
  ACCOUNT_CHANGE_PASSWORD_URL,
  ACCOUNT_DELETE_URL,
  ACCOUNT_ADDRESSES_URL,
  ACCOUNT_PAYMENT_METHODS_URL,
} from './account.constants';
import {
  PasswordChangeParameter,
  ResendActivationEmailParam,
  UserDetails,
  CreateUserParam,
  RequestPasswordResetParam,
  RequestAdminPasswordResetParam,
  FinishPasswordResetParam,
} from './account.interfaces';
import {
  useAxiosPostRequest,
  useAxiosGetRequest,
  AxiosHookResponse,
  useAxiosPutRequest,
} from '../../utils/axios-request.hooks';
import { useSWRAxiosGetRequest } from '../../utils/swr-axios-request.hooks';
import { API_COMMON_BASE_URL, UNAUTHORIZED_STATUS } from '../../utils/api.constants';
import { AddressDetails } from '../address';
import { PaymentMethodDetails } from '../payment-method';

const REVALIDATE_ONLY_ON_MOUNT_SWR_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnMount: true,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  refreshWhenOffline: false,
  refreshWhenHidden: false,
  refreshInterval: 0,
  errorRetryCount: 2,
};

/**
 * Register account api hook
 */
export const useRegisterAccount = (): AxiosHookResponse<CreateUserParam, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_REGISTER_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Resend activation email api hook
 */
export const useResendActivationEmail = (): AxiosHookResponse<ResendActivationEmailParam, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ACTIVATE_URL}/email`;

  return useAxiosPostRequest(url);
};

/**
 * Password reset init api hook
 */
export const usePasswordResetInit = (): AxiosHookResponse<RequestPasswordResetParam, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_RESET_PASSWORD_INIT_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Admin password reset init api hook (shop admin). The target shop is resolved server-side from the user record.
 */
export const useAdminPasswordResetInit = (): AxiosHookResponse<RequestAdminPasswordResetParam, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_RESET_PASSWORD_INIT_ADMIN_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Password reset finish api hook
 */
export const usePasswordResetFinish = (): AxiosHookResponse<FinishPasswordResetParam, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_RESET_PASSWORD_FINISH_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Acccount activate api hook
 */
export const useAccountActivate = (key: string): AxiosHookResponse<void, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ACTIVATE_URL}?key=${key}`;

  return useAxiosGetRequest(url);
};

/**
 * Acccount api hook
 * @param shouldFetch whether the account must be fetched
 * @param onUnauthorized called once when the api rejects the session, never on transient errors (network, 5xx)
 */
export const useGetAccount = (
  shouldFetch = false,
  onUnauthorized?: () => void
): SWRResponse<UserDetails, AxiosError> => {
  const urlOptional = shouldFetch ? `${API_COMMON_BASE_URL}/${ACCOUNT_URL}` : null;

  const response: SWRResponse<UserDetails, AxiosError> = useSWRAxiosGetRequest(
    urlOptional,
    REVALIDATE_ONLY_ON_MOUNT_SWR_OPTIONS
  );
  const isSessionRejected = response.error?.response?.status === UNAUTHORIZED_STATUS;

  useEffect(() => {
    if (!isSessionRejected || !onUnauthorized) return;

    onUnauthorized();
  }, [isSessionRejected]);

  return response;
};

/**
 * Get Acccount api
 */
export const getAccount = (): Promise<AxiosResponse<UserDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_URL}`;

  return axios.get(url);
};

/**
 * Save account api hook
 */
export const useSaveAccount = (): AxiosHookResponse<UserDetails, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Change password api hook
 */
export const useChangePassword = (): AxiosHookResponse<PasswordChangeParameter, void> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_CHANGE_PASSWORD_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Acccount address api hook
 */
export const useGetAccountAddress = (id: number): SWRResponse<AddressDetails, AxiosError> => {
  const urlOptional = (id && `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}/${id}`) || null;

  return useSWRAxiosGetRequest<AddressDetails>(urlOptional, REVALIDATE_ONLY_ON_MOUNT_SWR_OPTIONS);
};

/**
 * Acccount addresses api hook
 */
export const useGetAccountAddresses = (shouldFetch = true): SWRResponse<AddressDetails[], AxiosError> => {
  const urlOptional = (shouldFetch && `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}`) || null;

  return useSWRAxiosGetRequest<AddressDetails[]>(urlOptional, REVALIDATE_ONLY_ON_MOUNT_SWR_OPTIONS);
};

/**
 * Create address api hook
 */
export const useCreateAccountAddress = (): AxiosHookResponse<AddressDetails, AddressDetails> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Update address api hook
 */
export const useUpdateAccountAddress = (): AxiosHookResponse<AddressDetails, AddressDetails> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}`;

  return useAxiosPutRequest(url);
};

/**
 * Update address api
 * @param address the update address param
 * @returns the address updated
 */
export const updateAccountAddress = (address: AddressDetails): Promise<AxiosResponse<AddressDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}`;

  return axios.put(url, address);
};

/**
 * Delete address api
 */
export const deleteAccountAddress = (id: number): Promise<AxiosResponse<void>> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_ADDRESSES_URL}/${id}`;

  return axios.delete(url);
};

/**
 * Get acccount payment methods api hook
 */
export const useGetPaymentMethods = (shouldFetch = true): SWRResponse<PaymentMethodDetails[], AxiosError> => {
  const urlOptional = (shouldFetch && `${API_COMMON_BASE_URL}/${ACCOUNT_PAYMENT_METHODS_URL}`) || null;

  return useSWRAxiosGetRequest<PaymentMethodDetails[]>(urlOptional, REVALIDATE_ONLY_ON_MOUNT_SWR_OPTIONS);
};

/**
 * Delete account api hook
 */
export const deleteAccount = (): Promise<AxiosResponse<void>> => {
  const url = `${API_COMMON_BASE_URL}/${ACCOUNT_DELETE_URL}`;

  return axios.delete(url);
};
